import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, CircularProgress } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import { bool, func } from "prop-types";
import { getCountryAction } from "../../redux/actions/countryActions";
import { updateProfileAction, getProfileAction } from "../../redux/actions/profileActions";
import { getAccountBalance } from "../../redux/actions/walletActions";
import SearchableCountrySelect from "../SearchableCountrySelect";
import { sortCountriesWithPriority } from "../../utils/countryHelpers";
import "./index.scss";

const CountryGateModal = ({ open, onClose, onSuccess, allowDismiss = true }) => {
  const dispatch = useDispatch();
  const rawCountries = useSelector((store) => store?.country?.countries);
  const countries = useMemo(() => (Array.isArray(rawCountries) ? rawCountries : []), [rawCountries]);

  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && countries.length === 0) {
      getCountryAction(dispatch);
    }
  }, [open, countries.length, dispatch]);

  const sortedCountries = useMemo(() => {
    return sortCountriesWithPriority(countries);
  }, [countries]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!selectedCountryId) {
      cogoToast.error("Please select a country to proceed");
      return;
    }

    setLoading(true);
    try {
      const response = await updateProfileAction({ country: selectedCountryId }, dispatch);

      if (response?.type === "ERROR" || response?.error) {
        cogoToast.error(response?.payload || "Failed to save country. Please try again.");
      } else {
        cogoToast.success("Country saved and wallet activated!");
        // Refresh profile & wallet balance
        await Promise.all([
          getProfileAction(dispatch),
          getAccountBalance(dispatch),
        ]);

        if (onSuccess) {
          onSuccess(selectedCountryId);
        } else if (onClose) {
          onClose();
        }
      }
    } catch (_err) {
      cogoToast.error("An error occurred while saving your country");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={allowDismiss ? onClose : undefined}
      disableBackdropClick={!allowDismiss}
      disableEscapeKeyDown={!allowDismiss}
      className="country-gate-dialog"
    >
      <DialogContent>
        <div className="country-gate-header">
          <div className="gate-icon-wrapper">
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <h3>Select Your Country</h3>
          <p>
            Choose your primary country to activate your wallet and start receiving payouts, referral rewards, and earnings.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="country-gate-body">
          <SearchableCountrySelect
            countries={sortedCountries}
            value={selectedCountryId}
            onChange={(e) => setSelectedCountryId(e.target.value)}
            label="Country"
            name="country"
            required
            placeholder="Select or search country..."
          />

          <div className="gate-helper-notice">
            <svg
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              You’ll need to verify your phone number to change this later.
            </span>
          </div>

          <button
            type="submit"
            className="gate-submit-btn"
            disabled={loading || !selectedCountryId}
          >
            {loading ? (
              <>
                <CircularProgress size={18} color="inherit" />
                <span>Activating Wallet...</span>
              </>
            ) : (
              <span>Activate Wallet</span>
            )}
          </button>

          {allowDismiss && onClose && (
            <button
              type="button"
              className="gate-dismiss-link"
              onClick={onClose}
            >
              I’ll do this later
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

CountryGateModal.propTypes = {
  open: bool.isRequired,
  onClose: func,
  onSuccess: func,
  allowDismiss: bool,
};

CountryGateModal.defaultProps = {
  allowDismiss: true,
};

export default CountryGateModal;
